import {
  createReview,
  deleteReviewUuid,
  searchReview,
  type ReviewCreateUser,
  type ReviewInDb,
} from '@/api';
import { useOrganizationStore } from '@/stores/organizationStore';
import { defineStore } from 'pinia';

// Define specific column types to avoid 'any'
interface ColumnData {
  ai_value: string | number | null;
  ai_reason: string | null;
  ai_page: number | null;
  ai_score: number | null;
  ground_truth: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  run_time_s: number | null;
  extraction_prompt: string | null;
}

// Define file info type
interface FileInfo {
  file_name: string;
  page_count: number;
  estimated_input_tokens: number;
}

// Define our MeasurementResult interface based on the structure in ExtractionValidation.vue
interface MeasurementResult {
  name: string;
  results: Record<
    string,
    {
      file_info: FileInfo;
      columns: Record<string, ColumnData>;
    }
  >;
}

/**
 * Store to manage reviews data and related operations
 */
export const useReviewsStore = defineStore('reviews', {
  state: () => ({
    reviews: [] as ReviewInDb[],
    currentResult: null as ReviewInDb | null,
    activeResult: null as MeasurementResult | null,
    loading: false,
    error: null as string | null,
  }),
  getters: {
    sortedReviews: (state): ReviewInDb[] => {
      return [...state.reviews].sort((a, b) => {
        // Sort by name alphabetically
        return a.name.localeCompare(b.name);
      });
    },
    findReviewByName: state => {
      return (name: string): ReviewInDb | undefined => {
        return state.reviews.find(review => review.name === name);
      };
    },
    getReviewById: state => {
      return (uid: string): ReviewInDb | undefined => {
        return state.reviews.find(review => review.uid === uid);
      };
    },
  },
  actions: {
    /**
     * Fetches all reviews
     * @returns Promise<void>
     */
    async fetchReviews(): Promise<void> {
      try {
        this.loading = true;
        this.error = null;

        const response = await searchReview({ body: { text: '' } });

        if (!response.data) {
          throw new Error('Failed to fetch reviews');
        }
        const searchResult = response.data;
        this.reviews = searchResult.results;
      } catch (error) {
        let errorMessage: string;

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          const apiError = error as { detail?: string; message?: string };
          errorMessage =
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
            apiError.detail || apiError.message || 'Failed to load reviews. Please try again.';
        } else {
          errorMessage = 'Failed to load reviews. Please try again.';
        }

        this.error = errorMessage;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetches a specific review result by UID
     * @param uid - The UUID of the review to fetch
     * @returns Promise<ReviewInDb | null> - The review or null if not found
     */

    /**
     * Creates a new review
     * @param reviewData - The review data to create
     * @returns Promise<string | boolean> - The review UID if successful, false otherwise
     */
    async createReview(reviewData: ReviewCreateUser): Promise<string | boolean> {
      try {
        this.loading = true;
        this.error = null;

        const organizationStore = useOrganizationStore();
        const response = await createReview({ body: reviewData });

        if (!response.data) {
          throw new Error('Failed to create review');
        }
        const newReview = response.data;
        this.reviews.push(newReview);

        // Update organization allowance usage after successful review
        if (organizationStore.activeOrganization?.uid) {
          await organizationStore.fetchOwnActiveOrganizationAllowanceUsage();
        }

        return newReview.uid;
      } catch (error) {
        let errorMessage: string;

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          const apiError = error as { detail?: string; message?: string };
          errorMessage =
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
            apiError.detail || apiError.message || 'Failed to create review. Please try again.';
        } else {
          errorMessage = 'Failed to create review. Please try again.';
        }

        this.error = errorMessage;
        return false;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Deletes a review by its UUID
     * @param uid - The UUID of the review to delete
     * @returns Promise<boolean> - true if delete succeeded, false otherwise
     */
    async deleteReview(uid: string): Promise<boolean> {
      try {
        this.loading = true;
        this.error = null;

        await deleteReviewUuid({ path: { review_uid: uid } });

        this.reviews = this.reviews.filter(review => review.uid !== uid);
        return true;
      } catch (error) {
        let errorMessage: string;

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          const apiError = error as { detail?: string; message?: string };
          errorMessage =
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
            apiError.detail || apiError.message || 'Failed to delete review. Please try again.';
        } else {
          errorMessage = 'Failed to delete review. Please try again.';
        }

        this.error = errorMessage;
        return false;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Updates the store based on WebSocket events
     * @param event - The Gaia event received from WebSocket
     * @param reviewId - Optional review ID to filter updates
     * @returns Promise<void>
     */

    /**
     * Resets the store state
     */
    reset(): void {
      this.reviews = [];
      this.currentResult = null;
      this.activeResult = null;
      this.loading = false;
      this.error = null;
    },
  },
});
